import React, { forwardRef } from 'react';
import { Alert, Box, Button, Card, Divider, TextField, Tooltip, Typography } from '@mui/material';
import { InfoOutlined, Warning } from '@mui/icons-material';
import { format, isValid, parse } from 'date-fns';
import DrawLineItemsTable from '../project/DrawLineItemsTable';
import { usePayappProcess } from '../../context/PayappProcessContext';
import PayappBudget from '../file-upload/PayappBudget';
import { ProjectLineItemDTO } from '../project/project.type';
import SimpleNotes, { SimpleNotesRef } from '../general/Notes/SimpleNotes';
import { ANALYTICS_EVENTS } from '../../services/analyticsService';
import DynamicDatePicker from '../common/DynamicDatePicker';
import { parsePeriodDateToISO } from '../../common/utils/common.util';

export type PreviewAndConfirmRef = SimpleNotesRef;

interface PreviewAndConfirmProps {
  budgetSummaryExpanded?: boolean;
  onBudgetSummaryChange?: (expanded: boolean) => void;
}

const PreviewAndConfirm = forwardRef<PreviewAndConfirmRef, PreviewAndConfirmProps>(
  ({ budgetSummaryExpanded, onBudgetSummaryChange }, ref) => {
    const { state, setProjectDraw, updateCoverPageValue, updateState, pendingMergeEventsRef, payappSessionIdRef } = usePayappProcess();

    const assignedPayappColumns = React.useMemo(() => new Set(Object.values(state.fieldHeadersMapping || {})), [state.fieldHeadersMapping]);
    const filteredLineItems: ProjectLineItemDTO[] = React.useMemo(() => {
      return state.projectDraw.lineItems?.filter((item: any) => !item.isDeleted) || [];
    }, [state.projectDraw.lineItems]);

    const totalsLineItem: ProjectLineItemDTO = React.useMemo(
      () => ({
        id: 'totals',
        lineItem: {
          id: 'totals',
          description: 'Totals',
          division: undefined,
          unit: '',
        },
        originalBudget: filteredLineItems.reduce((acc, item) => acc + (item.originalBudget || 0), 0),
        reallocation: filteredLineItems.reduce((acc, item) => acc + (item.reallocation || 0), 0),
        changeOrder: filteredLineItems.reduce((acc, item) => acc + (item.changeOrder || 0), 0),
        previousApplication: filteredLineItems.reduce((acc, item) => acc + (item.previousApplication || 0), 0),
        completeThisPeriod: filteredLineItems.reduce((acc, item) => acc + (item.completeThisPeriod || 0), 0),
        storedThisPeriod: filteredLineItems.reduce((acc, item) => acc + (item.storedThisPeriod || 0), 0),
        retainage: filteredLineItems.reduce((acc, item) => acc + (item.retainage || 0), 0),
        revisedBudget: 0,
        cdccApproved: filteredLineItems.reduce((acc, item) => acc + (item.cdccApproved || 0), 0),
        completedStoredToDate: 0,
        percentageComplete: 0,
        balanceToComplete: 0,
        projectDrawId: '',
        projectDrawName: '',
        quantity: 0,
        isIncluded: true,
      }),
      [filteredLineItems],
    );

    const isMergeStep = state.activeStep === 2; // Step 3 "Merge Into Last Month" (0-based)

    const handleMerge = React.useCallback(
      (payappItem: ProjectLineItemDTO, projectItem?: ProjectLineItemDTO, source?: 'selected') => {
        if (source === 'selected' && state.mergeMetrics) {
          updateState({ mergeMetrics: { ...state.mergeMetrics, selected: state.mergeMetrics.selected + 1 } });
        }
        pendingMergeEventsRef.current.push({
          eventName: ANALYTICS_EVENTS.PAYAPP_LINE_ITEM_MERGED,
          properties: {
            project_id: String(state.projectId ?? ''),
            project_label: `${state.projectNumber ?? ''} - ${state.projectName ?? ''}`.trim(),
            draw_name: state.projectDraw?.projectDrawSummary?.name ?? '',
            payapp_session_id: payappSessionIdRef.current,
            merge_type: 'manual',
            fuzzy_match_type: 'description',
            payapp_description: payappItem.lineItem?.description ?? '',
            payapp_revised_budget: (payappItem.originalBudget ?? 0) + (payappItem.reallocation ?? 0) + (payappItem.changeOrder ?? 0),
            payapp_line_item_id: payappItem.id ?? '',
            project_description: projectItem?.lineItem?.description ?? '',
            project_revised_budget: projectItem
              ? (projectItem.originalBudget ?? 0) + (projectItem.reallocation ?? 0) + (projectItem.changeOrder ?? 0)
              : 0,
            project_line_item_id: projectItem?.id ?? '',
            fuzzy_score: payappItem.fuzzyScore,
            budget_match: payappItem.budgetMatch,
          },
        });
      },
      [
        pendingMergeEventsRef,
        payappSessionIdRef,
        state.projectId,
        state.projectNumber,
        state.projectName,
        state.projectDraw,
        state.mergeMetrics,
        updateState,
      ],
    );

    const renderManualCoverFields = () => {
      const previousAppNumber = state.originalProjectDraw?.projectDrawSummary?.applicationNumber;
      const prevPeriodEnding = state.originalProjectDraw?.projectDrawSummary?.periodEnding;

      const appNumValue = state.coverPageValues['Application Number'] ?? '';
      const periodBeginValue = state.coverPageValues['Period Beginning'] ?? '';
      const periodEndValue = state.coverPageValues['Period Ending'] ?? '';

      const handleAutofillPeriodBegin = () => {
        if (!prevPeriodEnding) return;
        try {
          const isoEnd = parsePeriodDateToISO(prevPeriodEnding);
          if (!isoEnd) return;
          const end = parse(isoEnd, 'yyyy-MM-dd', new Date());
          if (!isValid(end)) return;
          const begin = new Date(end);
          begin.setDate(begin.getDate() + 1);
          updateCoverPageValue('Period Beginning', format(begin, 'yyyy-MM-dd'));
        } catch {
          // ignore unparseable dates
        }
      };

      let appNumIcon = null;
      if (previousAppNumber !== undefined && previousAppNumber !== null) {
        const current = parseInt(String(appNumValue)) || 0;
        const previous = parseInt(String(previousAppNumber)) || 0;
        const diff = current - previous;
        appNumIcon =
          diff < 0 || diff > 1 ? (
            <Warning sx={{ fontSize: 16, color: 'orange', ml: 0.5 }} />
          ) : (
            <InfoOutlined sx={{ fontSize: 16, color: 'rgb(171 170 170)', ml: 0.5 }} />
          );
      }

      const fieldSx = {
        width: '160px',
        input: { fontSize: '1rem', textAlign: 'right', paddingRight: '8px', paddingTop: '5px', paddingBottom: '5px' },
      };

      const datePickerSx = {
        width: '160px',
        '& .MuiInputBase-input': {
          fontSize: '1rem !important',
          paddingTop: '5px !important',
          paddingBottom: '5px !important',
          paddingRight: '4px !important',
        },
        '& .MuiInputAdornment-root': { ml: 0 },
      };

      return (
        <Card variant='outlined' sx={{ p: 2, mb: 2 }}>
          <Box display='flex' alignItems='center'>
            {/* Application Number */}
            <Box display='flex' alignItems='center' gap={1} flex={1}>
              <Box display='flex' alignItems='center'>
                <Typography variant='subtitle2' fontWeight={600}>
                  Application Number
                </Typography>
                {appNumIcon && (
                  <Tooltip
                    title={
                      <Typography variant='inherit'>
                        Previous Month: <strong>{previousAppNumber}</strong>
                      </Typography>
                    }
                    arrow
                  >
                    {appNumIcon}
                  </Tooltip>
                )}
              </Box>
              <TextField
                variant='outlined'
                size='medium'
                value={appNumValue.toString()}
                onChange={e => updateCoverPageValue('Application Number', e.target.value)}
                sx={fieldSx}
              />
            </Box>
            <Divider orientation='vertical' flexItem />
            {/* Period Beginning */}
            <Box display='flex' alignItems='center' gap={1} flex={1}>
              <Box display='flex' alignItems='center' gap={0.5}>
                <Typography variant='subtitle2' fontWeight={400}>
                  Period Beginning
                </Typography>
                {prevPeriodEnding && (
                  <Tooltip title='Set to previous Period Ending + 1 day' arrow>
                    <Button
                      size='small'
                      variant='text'
                      onClick={handleAutofillPeriodBegin}
                      sx={{ fontSize: '0.7rem', py: 0, px: 0.5, textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      Autofill from last month
                    </Button>
                  </Tooltip>
                )}
              </Box>
              <Box sx={datePickerSx}>
                <DynamicDatePicker
                  fieldName='Period Beginning'
                  value={parsePeriodDateToISO(periodBeginValue.toString())}
                  onChange={(field, val) => updateCoverPageValue(field, val ?? '')}
                />
              </Box>
            </Box>
            <Divider orientation='vertical' flexItem />
            {/* Period Ending */}
            <Box display='flex' alignItems='center' gap={1} flex={1}>
              <Typography variant='subtitle2' fontWeight={400}>
                Period Ending
              </Typography>
              <Box sx={datePickerSx}>
                <DynamicDatePicker
                  fieldName='Period Ending'
                  value={parsePeriodDateToISO(periodEndValue.toString())}
                  onChange={(field, val) => updateCoverPageValue(field, val ?? '')}
                />
              </Box>
            </Box>
          </Box>
        </Card>
      );
    };

    return (
      <Box>
        {state.isManualUpdate && (
          <Alert severity='info' sx={{ mb: 2 }}>
            Manual PayApp Update — enter Complete this Period and Stored this Period values below. Previous Application has been rolled
            forward automatically.
          </Alert>
        )}
        {state.isManualUpdate && renderManualCoverFields()}
        <PayappBudget
          coverPageValues={state.coverPageValues}
          updateCoverPageValue={updateCoverPageValue}
          projectDraw={state.projectDraw}
          setProjectDraw={setProjectDraw}
          totalsLineItem={totalsLineItem}
          originalProjectDraw={state.originalProjectDraw}
          hideApplicationNumber
          hideBudgetSummary={isMergeStep}
          hidePayappComparison={state.isManualUpdate}
          isSecondPayappFlow={state.isSecondPayappFlow}
          firstPayappCoverPageValues={state.firstPayappSnapshot?.coverPageValues ?? null}
          expanded={budgetSummaryExpanded}
          onExpandedChange={onBudgetSummaryChange}
        />

        {state.projectDraw && (
          <DrawLineItemsTable
            projectDraw={state.projectDraw}
            setProjectDraw={setProjectDraw}
            divisionsData={state.divisions}
            filteredLineItems={filteredLineItems}
            totalsLineItem={totalsLineItem}
            drawInputRetainagePercentage={state.projectDraw.projectDrawSummary?.inputRetainagePercentage}
            isSecondPayappFlow={state.isSecondPayappFlow}
            assignedPayappColumns={assignedPayappColumns}
            handleSkippedPayapp={!!state.handleSkippedPayapp}
            onMerge={handleMerge}
            isManualUpdate={!!state.isManualUpdate}
          />
        )}

        <SimpleNotes ref={ref} initialContent={state.notes[0]?.content || ''} />
      </Box>
    );
  },
);

PreviewAndConfirm.displayName = 'PreviewAndConfirm';

export default PreviewAndConfirm;
